import {
  POP_AUTHORITY_BOOSTERS,
  popAuthorityBoosterIsVisible,
  popAuthorityBoosterSort,
  popAuthorityBoostersForSale,
  showPopAuthorityToast,
} from '@/utils/popAuthority'

/**
 * Shared state of the Boosters tab and the Deliverables → Boosters sub-tab (Phase 8): one fetch of
 * GET /api/pop-authority/sites/:id/boosters/, and every mutation returns that same payload, so the
 * screens never patch rows locally (same rule as Deliverables.runAction).
 */
export default {
  inject: {
    refreshWhiteGloveOrders: { default: null },
  },
  data () {
    return {
      boostersLoading: false,
      boostersError: '',
      boostersState: null,
      boostersBusy: false,
    }
  },
  computed: {
    boosterSiteId () {
      return Number(this.$route.params.siteId)
    },
    /** Visible (non-draft) booster orders, newest first. */
    boosterOrders () {
      const orders = (this.boostersState && this.boostersState.orders) || []
      return popAuthorityBoosterSort(orders.filter(popAuthorityBoosterIsVisible))
    },
    /** Live catalogue from the API, the offline mirror until it answers; boosters for sale only. */
    boosterCatalogue () {
      const catalogue = this.boostersState && this.boostersState.catalogue
      return popAuthorityBoostersForSale(
        Array.isArray(catalogue) && catalogue.length ? catalogue : POP_AUTHORITY_BOOSTERS)
    },
    /** The campaign manager's recommendation while it is open (not dismissed, not bought). */
    boosterRecommendation () {
      const rec = this.boostersState && this.boostersState.recommendation
      if (!rec || rec.dismissedAt || rec.open === false) return null
      return rec
    },
    boostersIsAdmin () {
      return Boolean(this.boostersState && this.boostersState.isAdmin)
    },
  },
  methods: {
    loadBoosters (opts = {}) {
      const silent = opts.silent === true
      if (!silent) {
        this.boostersLoading = true
        this.boostersError = ''
      }
      return this.$store
        .dispatch('getPopAuthorityBoosters', { siteId: this.boosterSiteId })
        .then((response) => {
          const data = (response && response.data) || {}
          if (data.status === 'SUCCESS') {
            this.applyBoosters(data)
          } else if (!silent) {
            this.boostersError = data.msg || 'Please try again in a moment.'
          }
          return data
        })
        .catch((e) => {
          if (!silent) {
            this.boostersError = (e && e.message) || 'Please try again in a moment.'
          }
          return null
        })
        .finally(() => {
          this.boostersLoading = false
        })
    },
    applyBoosters (data) {
      this.boostersState = data
    },
    /** Runs a booster action; on success applies the returned state and refreshes the site statuses. */
    runBoosterAction (action, payload, successText) {
      if (this.boostersBusy) return Promise.resolve(null)
      this.boostersBusy = true
      this.$store.commit('showLoading')
      return this.$store
        .dispatch(action, payload)
        .then((response) => {
          const data = (response && response.data) || {}
          if (data.status === 'SUCCESS') {
            if (Array.isArray(data.orders)) this.applyBoosters(data)
            // design: confirmations use the teal toast (PaToast in the layout); errors stay red
            // notifications (the app titles any other notification type "Error!")
            if (successText) showPopAuthorityToast(this, successText)
            if (typeof this.refreshWhiteGloveOrders === 'function') {
              this.refreshWhiteGloveOrders()
            }
            return data
          }
          this.$notify({
            group: 'info',
            type: 'error',
            text: data.msg || 'That did not work, please try again.',
          })
          if (Array.isArray(data.orders)) this.applyBoosters(data)
          return null
        })
        .catch((e) => {
          const data = (e && e.response && e.response.data) || {}
          const msg = data.msg || data.message || 'That did not work, please try again.'
          this.$notify({ group: 'info', type: 'error', text: msg })
          if (Array.isArray(data.orders)) this.applyBoosters(data)
          return null
        })
        .finally(() => {
          this.boostersBusy = false
          this.$store.commit('hideLoading')
        })
    },
  },
}
